import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Trash2, History, RotateCcw, Library, Hash, X } from 'lucide-react';

/**
 * Two-way bind the series + tag filter chips to URL search params so deep-links
 * like `/sermon-prep?series_id=Romans+Series` open Drafts already filtered.
 *
 * Returns [activeSeries, activeTag, setSeries, setTag]. Setting `null` clears
 * that param. We intentionally use `replace: true` so chip-toggling doesn't
 * pollute browser history with one entry per click.
 */
const useDeepLinkedFilters = () => {
  const [params, setParams] = useSearchParams();
  const series = params.get('series_id') || null;
  const tag = params.get('tag') || null;

  const update = (next) => {
    const merged = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') merged.delete(k);
      else merged.set(k, v);
    });
    setParams(merged, { replace: true });
  };

  // When the dialog mounts we surface the URL-derived value, so refreshing
  // the page or hitting the link cold preserves the filter — no extra effect
  // needed because the controlled getters above are already read off `params`.
  useEffect(() => { /* no-op — included for shape parity */ }, [series, tag]);

  return [series, tag, (s) => update({ series_id: s }), (t) => update({ tag: t })];
};


/**
 * Inline strip showing chip filters for series and tags derived from the
 * current drafts list. Clicking a chip narrows the list; clicking the active
 * chip again clears the filter. Only renders when ≥1 draft has any series
 * or tag attached, so empty states stay clean.
 */
const DraftFilterStrip = ({
  language,
  drafts,
  activeSeries,
  activeTag,
  onSelectSeries,
  onSelectTag,
}) => {
  const { t } = useLanguage();
  const seriesList = useMemo(() => {
    const set = new Set();
    drafts.forEach((d) => { if (d.series_id) set.add(d.series_id); });
    // Surface the active filter even when no drafts match it, so deep-links
    // to a series/tag the user no longer has don't render an empty strip.
    if (activeSeries) set.add(activeSeries);
    return [...set].sort();
  }, [drafts, activeSeries]);

  const tagList = useMemo(() => {
    const set = new Set();
    drafts.forEach((d) => (d.tags || []).forEach((tag) => set.add(tag)));
    if (activeTag) set.add(activeTag);
    return [...set].sort();
  }, [drafts, activeTag]);

  if (seriesList.length === 0 && tagList.length === 0) return null;

  return (
    <div
      className="space-y-2 pb-3 border-b border-border/60"
      data-testid="sermon-drafts-filter-strip"
    >
      {seriesList.length > 0 && (
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 mt-1.5">
            <Library className="w-3 h-3" />
            {t('sermon.series')}
          </span>
          {seriesList.map((s) => {
            const active = activeSeries === s;
            return (
              <Badge
                key={s}
                variant={active ? 'default' : 'secondary'}
                className="text-[11px] cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => onSelectSeries(active ? null : s)}
                data-testid={`drafts-filter-series-${s}`}
              >
                {s}
                {active && <X className="w-2.5 h-2.5 ml-1 opacity-70" />}
              </Badge>
            );
          })}
        </div>
      )}
      {tagList.length > 0 && (
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 mt-1.5">
            <Hash className="w-3 h-3" />
            {t('sermon.tags')}
          </span>
          {tagList.map((tag) => {
            const active = activeTag === tag;
            return (
              <Badge
                key={tag}
                variant={active ? 'default' : 'outline'}
                className="text-[11px] cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => onSelectTag(active ? null : tag)}
                data-testid={`drafts-filter-tag-${tag}`}
              >
                #{tag}
                {active && <X className="w-2.5 h-2.5 ml-1 opacity-70" />}
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Drafts dialog for the Sermon Prep tool — lists all saved drafts with
 * inline expandable revision history per draft.
 */
const SermonDraftsDialog = ({
  open,
  onOpenChange,
  language,
  draftId,
  draftsList,
  loadingDrafts,
  revisionsForId,
  revisions,
  loadingRevisions,
  onLoadDraft,
  onDeleteDraft,
  onOpenRevisions,
  onRevertTo,
}) => {
  const { t } = useLanguage();
  const [activeSeriesFilter, activeTagFilter, setSeries, setTag] = useDeepLinkedFilters();

  const filteredDrafts = useMemo(() => {
    return draftsList.filter((d) => {
      if (activeSeriesFilter && d.series_id !== activeSeriesFilter) return false;
      if (activeTagFilter && !(d.tags || []).includes(activeTagFilter)) return false;
      return true;
    });
  }, [draftsList, activeSeriesFilter, activeTagFilter]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="sermon-drafts-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('sermon.myDrafts')}
          </DialogTitle>
          <DialogDescription>
            {t('sermon.autosaveHint')}
          </DialogDescription>
        </DialogHeader>
        <DraftFilterStrip
          language={language}
          drafts={draftsList}
          activeSeries={activeSeriesFilter}
          activeTag={activeTagFilter}
          onSelectSeries={setSeries}
          onSelectTag={setTag}
        />
        <div className="max-h-[60vh] overflow-y-auto space-y-2">
          {loadingDrafts && (
            <p className="text-sm text-muted-foreground text-center py-6">
              <Loader2 className="inline w-4 h-4 animate-spin mr-2" />
              {t('sermon.loading')}
            </p>
          )}
          {!loadingDrafts && draftsList.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">
              {t('sermon.noDraftsYet')}
            </p>
          )}
          {!loadingDrafts && draftsList.length > 0 && filteredDrafts.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="drafts-empty-filter">
              {t('sermon.noMatchFilters')}
            </p>
          )}
          {filteredDrafts.map((d) => {
            const active = d.draft_id === draftId;
            const showingRevs = d.draft_id === revisionsForId;
            const when = d.updated_at
              ? new Date(d.updated_at * 1000).toLocaleString()
              : '';
            return (
              <div key={d.draft_id} className="space-y-1">
                <div
                  className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-sm ${
                    active
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-border hover:border-primary/50 hover:bg-muted/40'
                  }`}
                  data-testid={`sermon-draft-${d.draft_id}`}
                >
                  <button
                    onClick={() => onLoadDraft(d.draft_id)}
                    className="flex flex-col min-w-0 flex-1 text-left"
                    data-testid={`sermon-draft-load-${d.draft_id}`}
                  >
                    <span className="font-medium truncate">
                      {d.topic || t('sermon.untitled')}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {t('sermon.updated')} {when}
                      {active && t('sermon.current')}
                    </span>
                  </button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-muted-foreground hover:text-purple-600"
                    onClick={() => onOpenRevisions(d.draft_id)}
                    title={t('sermon.revisionHistory')}
                    data-testid={`sermon-draft-history-${d.draft_id}`}
                  >
                    <History className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-muted-foreground hover:text-red-500"
                    onClick={() => onDeleteDraft(d.draft_id)}
                    data-testid={`sermon-draft-delete-${d.draft_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>

                {showingRevs && (
                  <div
                    className="ml-3 pl-3 border-l-2 border-purple-300/60 dark:border-purple-700/60 space-y-1"
                    data-testid={`sermon-revisions-${d.draft_id}`}
                  >
                    {loadingRevisions && (
                      <p className="text-xs text-muted-foreground py-2">
                        <Loader2 className="inline w-3 h-3 animate-spin mr-1" />
                        {t('sermon.loading')}
                      </p>
                    )}
                    {!loadingRevisions && revisions.length === 0 && (
                      <p className="text-xs text-muted-foreground py-2">
                        {t('sermon.noRevisions')}
                      </p>
                    )}
                    {revisions.slice().reverse().map((r) => {
                      const ts = r.saved_at
                        ? new Date(r.saved_at * 1000).toLocaleString()
                        : '';
                      const preview = (r.notes || r.topic || '').slice(0, 60);
                      return (
                        <div
                          key={r.revision_id}
                          className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md bg-muted/30 hover:bg-muted/60 text-xs"
                          data-testid={`sermon-revision-${r.revision_id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-medium truncate">{ts}</p>
                            <p className="text-[11px] text-muted-foreground truncate">
                              {preview || t('sermon.empty')}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs"
                            onClick={() => onRevertTo(d.draft_id, r.revision_id)}
                            data-testid={`sermon-revision-revert-${r.revision_id}`}
                          >
                            <RotateCcw className="w-3 h-3 mr-1" />
                            {t('sermon.restore')}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SermonDraftsDialog;
